import { useMemo, useState } from "react";
import { Card, Input, InputNumber, Select, Table, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import api from "../interceptors/api";
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from "../components/ProtectedToolWrapper";

const { TextArea } = Input;

const MAX_SEEDS = 5;
const MAX_METRICS_KEYWORDS = 100;

// Google Ads geoTargetConstant ids for countries are 2000 + the ISO 3166-1
// numeric code. An empty value means worldwide.
const COUNTRIES = [
  { value: "", label: "Worldwide" },
  { value: "2840", label: "United States" },
  { value: "2356", label: "India" },
  { value: "2826", label: "United Kingdom" },
  { value: "2124", label: "Canada" },
  { value: "2036", label: "Australia" },
  { value: "2276", label: "Germany" },
  { value: "2250", label: "France" },
  { value: "2724", label: "Spain" },
  { value: "2380", label: "Italy" },
  { value: "2528", label: "Netherlands" },
  { value: "2076", label: "Brazil" },
  { value: "2484", label: "Mexico" },
  { value: "2392", label: "Japan" },
  { value: "2702", label: "Singapore" },
  { value: "2784", label: "United Arab Emirates" },
];

// Google Ads languageConstant ids.
const LANGUAGES = [
  { value: "1000", label: "English" },
  { value: "1003", label: "Spanish" },
  { value: "1002", label: "French" },
  { value: "1001", label: "German" },
  { value: "1004", label: "Italian" },
  { value: "1014", label: "Portuguese" },
  { value: "1010", label: "Dutch" },
  { value: "1005", label: "Japanese" },
  { value: "1012", label: "Korean" },
  { value: "1017", label: "Chinese (Simplified)" },
  { value: "1023", label: "Hindi" },
  { value: "1019", label: "Arabic" },
  { value: "1031", label: "Russian" },
];

const COMPETITION_COLORS = { LOW: "green", MEDIUM: "gold", HIGH: "red", UNKNOWN: "default" };

const splitKeywords = (text) =>
  [...new Set(
    String(text || "")
      .split(/[\n,]+/)
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean)
  )];

// Percentage change of the last 3 months vs the 3 before them, or null when
// there isn't enough history to compare.
const trendPercent = (months = []) => {
  if (months.length < 6) return null;
  const recent = months.slice(-3).reduce((a, m) => a + m.searches, 0);
  const prior = months.slice(-6, -3).reduce((a, m) => a + m.searches, 0);
  if (prior === 0) return recent === 0 ? 0 : null;
  return Math.round(((recent - prior) / prior) * 100);
};

const Sparkline = ({ months = [] }) => {
  if (months.length < 2) return <span className="text-gray-400">–</span>;
  const w = 96;
  const h = 24;
  const max = Math.max(...months.map((m) => m.searches), 1);
  const step = w / (months.length - 1);
  const points = months
    .map((m, i) => `${(i * step).toFixed(1)},${(h - (m.searches / max) * (h - 2) - 1).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline fill="none" stroke="#2563eb" strokeWidth="1.5" points={points} />
    </svg>
  );
};

const formatBid = (low, high) => {
  if (low == null && high == null) return "–";
  const fmt = (v) => (v == null ? "?" : v.toFixed(2));
  return `${fmt(low)} – ${fmt(high)}`;
};

const KeywordResearch = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("ideas");
  const [seedText, setSeedText] = useState("");
  const [country, setCountry] = useState("2840");
  const [language, setLanguage] = useState("1000");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("");
  const [minVolume, setMinVolume] = useState(0);

  const seeds = useMemo(() => splitKeywords(seedText), [seedText]);
  const maxSeeds = mode === "ideas" ? MAX_SEEDS : MAX_METRICS_KEYWORDS;

  const rows = useMemo(() => {
    if (!result?.keywords) return [];
    const needle = filter.trim().toLowerCase();
    return result.keywords
      .filter((k) => k.avgMonthlySearches >= (minVolume || 0))
      .filter((k) => !needle || k.text.includes(needle))
      .map((k) => ({ ...k, key: k.text, trend: trendPercent(k.monthlySearchVolumes) }));
  }, [result, filter, minVolume]);

  const handleSearch = async () => {
    if (seeds.length === 0) return toast.error(t("kw_enter_seed"));
    if (seeds.length > maxSeeds) return toast.error(t("kw_too_many_seeds", { max: maxSeeds }));

    setLoading(true);
    setResult(null);
    const toastId = toast.loading(t("kw_searching"));
    try {
      const res = await api.post("/keyword-research", { seeds, mode, country, language });
      setResult(res.data);
      toast.success(t("kw_results_count", { count: res.data.keywords.length }), { id: toastId });
    } catch (err) {
      const serverError = err.response?.data?.error || "";
      if (serverError.includes("Insufficient credits")) {
        toast.error(t("insufficient_credits_error"), { id: toastId });
      } else if (err.response?.status === 503) {
        toast.error(t("kw_not_configured"), { id: toastId });
      } else {
        toast.error(serverError || t("kw_failed"), { id: toastId });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rows.map((r) => r.text).join("\n"));
      toast.success(t("kw_copied", { count: rows.length }));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const handleExportCsv = () => {
    const header = ["keyword", "seed", "avg_monthly_searches", "trend_3m_pct", "competition", "competition_index", "low_top_of_page_bid", "high_top_of_page_bid"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((r) =>
      [r.text, r.seed, r.avgMonthlySearches, r.trend ?? "", r.competition, r.competitionIndex, r.lowTopOfPageBid ?? "", r.highTopOfPageBid ?? ""].map(escape).join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `keywords-${(result?.seeds || ["export"]).join("-").replace(/[^a-z0-9-]+/gi, "_").slice(0, 60)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: t("kw_col_keyword"),
      dataIndex: "text",
      key: "text",
      sorter: (a, b) => a.text.localeCompare(b.text),
      render: (text, row) => (
        <span>
          {text}
          {mode === "ideas" && result?.seeds?.length > 1 && (
            <Tag className="ml-2" color="blue">{row.seed}</Tag>
          )}
        </span>
      ),
    },
    {
      title: t("kw_col_volume"),
      dataIndex: "avgMonthlySearches",
      key: "avgMonthlySearches",
      align: "right",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.avgMonthlySearches - b.avgMonthlySearches,
      render: (v) => v.toLocaleString(),
    },
    {
      title: t("kw_col_trend"),
      dataIndex: "trend",
      key: "trend",
      sorter: (a, b) => (a.trend ?? -Infinity) - (b.trend ?? -Infinity),
      render: (v, row) => (
        <div className="flex items-center gap-2">
          <Sparkline months={row.monthlySearchVolumes} />
          {v != null && (
            <span className={v > 0 ? "text-green-600" : v < 0 ? "text-red-600" : "text-gray-500"}>
              {v > 0 ? "+" : ""}{v}%
            </span>
          )}
        </div>
      ),
    },
    {
      title: t("kw_col_competition"),
      dataIndex: "competition",
      key: "competition",
      filters: ["LOW", "MEDIUM", "HIGH", "UNKNOWN"].map((c) => ({ text: c, value: c })),
      onFilter: (value, row) => row.competition === value,
      render: (c) => <Tag color={COMPETITION_COLORS[c] || "default"}>{c}</Tag>,
    },
    {
      title: t("kw_col_comp_index"),
      dataIndex: "competitionIndex",
      key: "competitionIndex",
      align: "right",
      sorter: (a, b) => a.competitionIndex - b.competitionIndex,
    },
    {
      title: (
        <Tooltip title={t("kw_col_cpc_hint")}>
          <span>{t("kw_col_cpc")}</span>
        </Tooltip>
      ),
      key: "cpc",
      align: "right",
      sorter: (a, b) => (a.highTopOfPageBid ?? 0) - (b.highTopOfPageBid ?? 0),
      render: (_, row) => formatBid(row.lowTopOfPageBid, row.highTopOfPageBid),
    },
  ];

  return (
    <ProtectedToolWrapper>
      <div className="max-w-6xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          <p className="text-sm text-gray-600 m-0">{t("kw_intro")}</p>

          <div className="flex flex-col md:flex-row gap-4 mt-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("kw_seeds_label")}</label>
              <TextArea
                rows={4}
                value={seedText}
                onChange={(e) => setSeedText(e.target.value)}
                placeholder={mode === "ideas" ? t("kw_seeds_placeholder_ideas") : t("kw_seeds_placeholder_metrics")}
              />
              <p className="text-xs text-gray-500 mt-1 mb-0">
                {mode === "ideas" ? t("kw_seeds_hint_ideas", { max: MAX_SEEDS }) : t("kw_seeds_hint_metrics", { max: MAX_METRICS_KEYWORDS })}
                {" "}({seeds.length}/{maxSeeds})
              </p>
            </div>
            <div className="w-full md:w-64 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("kw_mode_label")}</label>
                <Select
                  className="w-full"
                  value={mode}
                  onChange={setMode}
                  options={[
                    { value: "ideas", label: t("kw_mode_ideas") },
                    { value: "metrics", label: t("kw_mode_metrics") },
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("kw_country_label")}</label>
                <Select className="w-full" value={country} onChange={setCountry} options={COUNTRIES} showSearch optionFilterProp="label" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("kw_language_label")}</label>
                <Select className="w-full" value={language} onChange={setLanguage} options={LANGUAGES} showSearch optionFilterProp="label" />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 mt-4">
            <StandardButton onClick={handleSearch} loading={loading} type="secondary" className="w-full md:w-auto">
              {t("kw_search_button")}
            </StandardButton>
          </div>
        </Card>

        {result && (
          <Card className="p-6 rounded-xl shadow-lg mt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800 m-0">
                  {t("kw_results_count", { count: rows.length })}
                  {result.cached && <Tag className="ml-2">{t("kw_cached_badge")}</Tag>}
                </h3>
                <p className="text-xs text-gray-500 m-0 mt-1">
                  {COUNTRIES.find((c) => c.value === result.country)?.label || "Worldwide"}
                  {" · "}
                  {LANGUAGES.find((l) => l.value === result.language)?.label || "English"}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  placeholder={t("kw_filter_placeholder")}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  allowClear
                  style={{ width: 200 }}
                />
                <InputNumber
                  min={0}
                  value={minVolume}
                  onChange={(v) => setMinVolume(v || 0)}
                  placeholder={t("kw_min_volume")}
                  addonBefore={t("kw_min_volume")}
                  style={{ width: 200 }}
                />
                <StandardButton type="secondary" variant="outlined" size="small" onClick={handleCopy} disabled={rows.length === 0}>
                  {t("kw_copy_keywords")}
                </StandardButton>
                <StandardButton type="secondary" variant="outlined" size="small" onClick={handleExportCsv} disabled={rows.length === 0}>
                  {t("kw_export_csv")}
                </StandardButton>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={rows}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 250] }}
              scroll={{ x: true }}
              locale={{ emptyText: t("kw_no_results") }}
            />
          </Card>
        )}
      </div>
    </ProtectedToolWrapper>
  );
};

export default KeywordResearch;
